// MindForge web — TypeScript port of logic/scoring.py.
//
// XP economy (2026-04 revision)
// -----------------------------
// • Difficulty → XP  is EXPONENTIAL:   base × growth^(difficulty-1)
// • XP → Level       is LINEAR:        (level - 1) × LEVEL_XP_STEP
//
// Numerical contract — must match logic/scoring.py exactly:
//   calcXpReward(0, 1)  === 2          calcXpReward(0, 5)  === 9
//   calcXpReward(0, 3)  === 4          calcXpReward(0, 10) === 51
//   calcXpReward(5, 5)  === 33         calcErrorPenalty(5) === 18
//   xpForLevel(3)       === 3000       xpForLevel(22)      === 31500
//
// Integer truncation: Python's int() truncates toward zero. For non-negative
// inputs (always true here) Math.trunc() matches exactly. Do NOT use Math.floor
// — it would round differently for negative values if we ever fed any in.

export const XP_PER_CORRECT = 10;
export const STREAK_BONUS_MULT = 0.5;

// Difficulty-weighted XP (EXPONENTIAL).
export const DIFFICULTY_XP_BASE = 0.25;
export const DIFFICULTY_XP_GROWTH = 1.4;

// Level XP (LINEAR with a slow slope).
export const LEVEL_XP_STEP = 1500;

// Error penalty ratio: penalty = base_reward × PENALTY_RATIO.
// At 2.0× break-even is 66.7% accuracy.
export const PENALTY_RATIO = 2.0;

/** Total cumulative XP needed to reach this level (linear). */
export function xpForLevel(level: number): number {
  if (level <= 1) return 0;
  return (level - 1) * LEVEL_XP_STEP;
}

/** Compute current level from total XP (linear inverse). */
export function levelFromXp(totalXp: number): number {
  if (totalXp <= 0) return 1;
  return Math.trunc(totalXp / LEVEL_XP_STEP) + 1;
}

/**
 * Scale factor for XP based on task difficulty (exponential).
 * Diff 1 → 0.25x · Diff 5 → ~0.96x · Diff 10 → ~5.17x · Diff 15 → ~27.78x.
 */
export function difficultyMultiplier(difficulty: number): number {
  const exponent = Math.max(0, difficulty - 1);
  return DIFFICULTY_XP_BASE * Math.pow(DIFFICULTY_XP_GROWTH, exponent);
}

/** XP reward for a correct answer, scaled by difficulty and streak. */
export function calcXpReward(streak: number, difficulty = 1): number {
  const safeStreak = Math.max(0, streak);
  const reward =
    XP_PER_CORRECT *
    difficultyMultiplier(difficulty) *
    (1 + safeStreak * STREAK_BONUS_MULT);
  // Ensure at least 1 XP per correct answer — never award zero for a win.
  return Math.max(1, Math.trunc(reward));
}

/**
 * XP penalty for a wrong answer, scaled by difficulty.
 * Penalty = 2× the base reward at the given difficulty (no streak bonus).
 */
export function calcErrorPenalty(difficulty = 1): number {
  const baseReward = Math.max(
    1,
    Math.trunc(XP_PER_CORRECT * difficultyMultiplier(difficulty)),
  );
  return Math.trunc(baseReward * PENALTY_RATIO);
}

/**
 * XP penalty for a wrong math answer in Dual Task.
 * Scales with streak and difficulty, capped to the equivalent correct-answer
 * reward so it never punishes more than a single correct would have earned.
 */
export function calcMathPenalty(streak: number, difficulty = 1): number {
  const base = Math.max(
    1,
    Math.trunc(XP_PER_CORRECT * difficultyMultiplier(difficulty) * 0.5),
  );
  const scaled = Math.max(
    1,
    Math.trunc(base * (1 + Math.max(0, streak) * 0.25)),
  );
  return Math.min(scaled, calcXpReward(streak, difficulty));
}

/** Progress fraction [0.0, 1.0) toward next level. */
export function xpProgress(totalXp: number): number {
  if (totalXp <= 0) return 0.0;
  return (totalXp % LEVEL_XP_STEP) / LEVEL_XP_STEP;
}

/** Whether accuracy warrants a difficulty increase. */
export function shouldIncreaseDifficulty(
  correctCount: number,
  totalRounds: number,
  threshold = 0.8,
): boolean {
  if (totalRounds <= 0) return false;
  return correctCount / totalRounds >= threshold;
}

/** Tracks current streak and best streak for a session. */
export class StreakTracker {
  current = 0;
  best = 0;

  hit(): void {
    this.current += 1;
    if (this.current > this.best) this.best = this.current;
  }

  miss(): void {
    this.current = 0;
  }

  reset(): void {
    this.current = 0;
    this.best = 0;
  }
}
